import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, ChevronRight } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  path: string;
}

const routeLabelMap: Record<string, string> = {
  "/meals": "My Meals",
  "/meal-plans": "Meal Calendar",
  "/shopping-lists": "Shopping Lists",
  "/settings": "Settings",
  "/about": "About",
  "/contact": "Contact",
  "/privacy": "Privacy",
  "/login": "Login",
  "/register": "Register",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
};

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Don't render on home page
  if (pathname === "/") return null;

  const breadcrumbItems =
    items ||
    [
      { label: "Home", path: "/" },
      { label: routeLabelMap[pathname] || pathname, path: pathname },
    ].filter((item) => item.label);

  return (
    <nav
      aria-label="Breadcrumb"
      className="bg-white border-b border-secondary-200 py-3 print:hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ol className="flex items-center space-x-2 text-sm text-secondary-600 overflow-hidden">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            const isFirst = index === 0;

            return (
              <li
                key={item.path + index}
                className="flex items-center shrink-0"
              >
                {index > 0 && (
                  <ChevronRight
                    data-testid="breadcrumb-separator"
                    className="h-4 w-4 mx-2 text-secondary-400 shrink-0"
                    aria-hidden="true"
                  />
                )}
                {isLast ? (
                  <span
                    className="font-medium text-secondary-900 truncate max-w-[150px] sm:max-w-[250px]"
                    aria-current="page"
                  >
                    {isFirst && (
                      <Home
                        className="h-4 w-4 inline mr-1 text-secondary-500"
                        aria-hidden="true"
                      />
                    )}
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.path}
                    className="flex items-center hover:text-primary-600 transition-colors truncate max-w-[150px] sm:max-w-[250px]"
                  >
                    {isFirst && (
                      <Home
                        className="h-4 w-4 inline mr-1 text-secondary-500"
                        aria-hidden="true"
                      />
                    )}
                    {item.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
